/**
 * SellerInfo Component - Story 7.3: Create Product Detail Page
 * Displays seller information with location details (NO contact information)
 */

import React from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { MapPin, Store } from 'lucide-react';
import { cn } from '@/lib/utils';
import type { SellerInfoProps } from '../types/publicMarketplace.types';

/**
 * SellerInfo Component
 * Displays basic seller information including name and location
 *
 * Features:
 * - Seller name display
 * - Location (district, state) with map icon
 * - NO contact details (phone, email) as per privacy requirements
 * - Clean card design
 * - Responsive layout
 *
 * Note: Contact information is intentionally excluded for privacy.
 * Users must submit an inquiry to connect with the seller.
 *
 * @component
 * @example
 * <SellerInfo
 *   seller={{
 *     id: 'seller-123',
 *     name: 'Raj Kumar',
 *     district: 'Bangalore',
 *     state: 'Karnataka'
 *   }}
 * />
 */
export const SellerInfo: React.FC<SellerInfoProps> = ({ seller, className }) => {
  return (
    <Card className={cn('overflow-hidden', className)}>
      <CardHeader className="bg-gradient-to-r from-gray-50 to-gray-100 border-b">
        <CardTitle className="flex items-center gap-2 text-lg">
          <Store className="h-5 w-5 text-primary" />
          Seller Information
        </CardTitle>
      </CardHeader>
      <CardContent className="p-6 space-y-4">
        {/* Seller Name */}
        <div>
          <h3 className="text-sm font-medium text-gray-500 mb-1">Seller Name</h3>
          <p className="text-lg font-semibold text-gray-900">{seller.name}</p>
        </div>

        {/* Location */}
        <div>
          <h3 className="text-sm font-medium text-gray-500 mb-2">Location</h3>
          <div className="flex items-start gap-2">
            <MapPin className="h-5 w-5 text-primary flex-shrink-0 mt-0.5" />
            <div>
              <p className="text-base text-gray-900 font-medium">{seller.district}</p>
              <p className="text-sm text-gray-600">{seller.state}, India</p>
            </div>
          </div>
        </div>

        {/* Contact Notice */}
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mt-4">
          <p className="text-sm text-blue-900">
            <span className="font-medium">Want to connect with this seller?</span>
            <br />
            Submit an inquiry using the form below, and the seller will contact you directly.
          </p>
        </div>

        {/* Divider */}
        <div className="border-t border-gray-200 pt-4 mt-4">
          <p className="text-xs text-gray-500 text-center">
            Seller contact information is kept private for security purposes.
          </p>
        </div>
      </CardContent>
    </Card>
  );
};

export default SellerInfo;
